import { Inject, Injectable } from "@angular/core";
import { User } from "@auth0/auth0-spa-js";
import { UIRouter } from "@uirouter/angular";
import { IUserGrantedPermissions, IUserPermissionsDto, IUserTenantPermissions } from "../../types/user";
import { StorageService } from "./storageService.service";
import { UserProfile } from "./userProfile";

/**
 * @ngDoc Service
 * @name UserFormat
 */
@Injectable()
export class UserFormatService {

    constructor(
        private _storage: StorageService
    ) {}

    /**
     * Given and apiUserData, returns a formated User Object.
     * @param apiUserData 
     * @returns Formated user data.
     */
    getFormatedUserData(apiUserData) {

        return {
            id: apiUserData.user_id,
            name: apiUserData.username,
            email: apiUserData.email,
            fullName: this._getUserFullName(apiUserData),
            picture: apiUserData.picture,
            updatedAt: apiUserData.updated_at,
            createdAt: apiUserData.created_at,
            lastLogin: apiUserData.last_login,
            languages: this._getUserLanguages(apiUserData),
            role: this._getUserRole(apiUserData),
            airlineId: this._getUserAirline(apiUserData),
            product: this._getUserProduct(apiUserData)
        }
    }

    /**
     * Returns a formated User Object given auth0UserInfo. This is the authenticated user info
     * @param auth0UserInfo 
     * @returns
     */
    getFormatedAuthUserData(auth0UserInfo: User, permissions: IUserPermissionsDto[]): UserProfile {
        const newClaims = auth0UserInfo['https://everymundo.com/jwt/claims'];
        const { tenants, grantedPermissions } = this._formatPermission(permissions);
        return new UserProfile({
            email: auth0UserInfo.email,
            emailVerified: auth0UserInfo.email_verified,
            firstName: auth0UserInfo.given_name,
            fullName: auth0UserInfo.name,
            grantedPermissions: grantedPermissions,
            grantedTenants: tenants,
            lastName: auth0UserInfo.family_name,
            nickName: auth0UserInfo.nickname,
            organizationAdministrator: newClaims ? newClaims['organization-administrator'] || false : false,
            organizationCode: newClaims ? newClaims['organization-code'] || "" : "",
            picture: auth0UserInfo.picture,
            platformManagedOrganizations: newClaims?.platform_managed_organizations || [],
            platformRoot: newClaims ? newClaims['platform-root'] || false : false,
            platformUser: newClaims ? newClaims['platform-user'] || false : false,
            productionWarning: true,
            tenantCode: tenants.find(t => t.length === 2) || tenants[0], // get the first tenant code with 2 characters  
            tenantId: null,
            updatedAt: auth0UserInfo.updated_at,
            userId: auth0UserInfo.sub,
        }, this._storage);
    }

    private _formatPermission(permissions: IUserPermissionsDto[]): { tenants: string[], grantedPermissions: IUserGrantedPermissions } {
        return permissions.reduce(
            (acc, item) => {
                const code = item.code.toLowerCase();
                acc.tenants.push(code);
                acc.grantedPermissions[code] = this._formatAppsAndRoles(item);
                return acc;
            },
            {
                tenants: [],
                grantedPermissions: {}
            }
        )
    }

    private _formatAppsAndRoles(item: IUserPermissionsDto): IUserTenantPermissions {
       return item.roles.reduce(
            (acc, role) => {
                const split =  role.split(':');
                const app = split[0]?.toLowerCase() || "";
                const appRole = split[1] || "";
                if(app) {
                    if(!acc.apps.includes(app)) acc.apps.push(app);
                    if(appRole && !acc.roles[app]) acc.roles[app] = [appRole];
                    else if (appRole) ( acc.roles[app] as string[]).push(appRole);
                }
                return acc;
            }, 
            {
                apps: [],
                roles: {}
            }
        ) 
    }

    /**
     * Returns a format User Object to send to the Api.
     * @param {object} user The format that the user object has in the app
     * @returns {Object} API formated user object.
     */
    getApiUserData(user, airlineId) {

        const apiUserFormat: { [index: string]:any } = {
            username: user.name,
            email: user.email,
            user_metadata: {
                fullname: user.fullName || ''
            },
            app_metadata: {
                airline_id: airlineId,
                trfx_roles: [this._formatApiUserRole(user.role)],
                languages: user.languages && user.languages.length > 0 ? user.languages : [],
                openair_apps: [{
                    name: user.product
                }]
            }
        }

        if (user.password) {
            apiUserFormat.password = user.password;
        }

        return apiUserFormat;
    }

    /**
     * Given an apiUserData, returns the user's fullName
     * @param apiUserData 
     * @returns User full name.
     */
    private _getUserFullName(apiUserData): string {
        if (apiUserData.user_metadata?.fullname) {
            return apiUserData.user_metadata.fullname as string;
        } 
        else if (apiUserData.app_metadata?.full_name) {
            return apiUserData.app_metadata.full_name as string;
        }
        else return 'UserFullName';
    }

    /**
     * Given apiUserData return users's languages.
     * @param apiUserData 
     * @returns User's languages.
     */
    private _getUserLanguages(apiUserData): Array<String> {
        if (apiUserData.user_metadata?.languages) {
            return apiUserData.user_metadata.languages as Array<string>;
        } 
        else if (apiUserData.app_metadata?.languages) {
            return apiUserData.app_metadata.languages as Array<string>;
        }
        else return [];
    }

    /**
     * Given an apiUserData, returns the users's role
     * @param apiUserData 
     * @returns User's role.
     */
    private _getUserRole(apiUserData): string {
        if (apiUserData.user_metadata?.roles) {
            return this._formatUserRole(apiUserData.user_metadata.roles[0]);
        } 
        else if (apiUserData.app_metadata?.trfx_roles) {
            return this._formatUserRole(apiUserData.app_metadata.trfx_roles[0]);
        }
        else return "";
    }

    /**
     * Returns an string as a formated role to the frontend 
     * @param role 
     * @returns Formated role.
     */
    private _formatUserRole(role: string): string {
        switch (role) {
            case 'ROLE_ADMIN_USER':
                return 'Root';
            case 'ROLE_AIRLINE_USER':
                return 'Admin';
            case 'ROLE_AIRLINE_EDITOR':
                return 'Editor';
            default:
                return "";
        }
    }

    /**
     * Given an apiUserData, returns the user's airline ID.
     * @param apiUserData 
     * @returns User's airline ID.
     */
    private _getUserAirline(apiUserData): string {     
        return (apiUserData.app_metadata?.airline_id) ? apiUserData.app_metadata.airline_id : '';
    }

    /**
     * Given an apiUserData, returns the formated users's product.
     * @param apiUserData
     * @returns User's product. 
     */
    private _getUserProduct(apiUserData): string {
        return (apiUserData.app_metadata?.openair_apps?.length > 0 && apiUserData.app_metadata.openair_apps[0].name) ? 
            apiUserData.app_metadata.openair_apps[0].name :
            'airTRFX';
    }

    /**
     * Returns an string as a formated role to the backend.
     * @param role 
     * @returns Backend formated role.
     */
    private _formatApiUserRole(role: string): string {
        switch (role) {
            case 'Root':
                return 'ROLE_ADMIN_USER';
            case 'Admin':
                return 'ROLE_AIRLINE_USER';
            case 'Editor':
                return 'ROLE_AIRLINE_EDITOR';
            default:
                return "";
        }
    }
}